import React, { useState, useCallback } from 'react';
import { Button, CircularProgress, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Sparkles } from 'lucide-react';
import { callFirebaseFunction } from '../../services/firebaseFunctions';
import { useFeatureEnabled } from '../../services/features';
import { useIsOrgAIEnabled } from '../../services/orgSettings';
import { useCurrentUser } from '../../services/currentUser';

const useStyles = makeStyles((theme) => ({
  button: {
    textTransform: 'none',
    fontSize: 13,
  },
}));

interface AIMessageDraftProps {
  context: {
    templateName?: string;
    taskName?: string;
    messageType?: string;
    timingDay?: number;
    recipientActor?: string;
  };
  onDraftGenerated: (content: { subject: string; body: string }) => void;
}

export const AIMessageDraft: React.FC<AIMessageDraftProps> = ({ context, onDraftGenerated }) => {
  const classes = useStyles();
  const { org } = useCurrentUser();
  const hasFlag = useFeatureEnabled('AI_MESSAGE_DRAFTS');
  const isAIEnabled = useIsOrgAIEnabled(org.id);
  const [loading, setLoading] = useState(false);

  const handleDraft = useCallback(async () => {
    setLoading(true);
    try {
      const prompt = buildPrompt(context);
      const result = await callFirebaseFunction('ai_generateMessage', {
        organization_id: org.id,
        prompt,
        tone: 'Friendly',
        length: 'Medium',
      });

      const data = result as { content: string };
      // Try to parse subject and body from the generated content
      const { subject, body } = parseGeneratedContent(data.content);
      onDraftGenerated({ subject, body });
    } catch (error) {
      console.error('AI draft failed:', error);
    } finally {
      setLoading(false);
    }
  }, [context, org.id, onDraftGenerated]);

  if (!hasFlag || !isAIEnabled) return null;

  return (
    <Tooltip title="Generate message content with AI">
      <Button
        className={classes.button}
        size="small"
        variant="outlined"
        color="primary"
        onClick={handleDraft}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : <Sparkles size={14} />}
      >
        {loading ? 'Drafting...' : 'Draft with AI'}
      </Button>
    </Tooltip>
  );
};

function buildPrompt(context: AIMessageDraftProps['context']): string {
  const parts = ['Write an email for an employee experience.'];
  if (context.templateName) parts.push(`Experience: ${context.templateName}.`);
  if (context.taskName) parts.push(`Related task: ${context.taskName}.`);
  if (context.timingDay !== undefined) parts.push(`Sent on Day ${context.timingDay} of the experience.`);
  if (context.recipientActor) parts.push(`Recipient: ${context.recipientActor}.`);
  if (context.messageType === 'reminder') parts.push('This is a reminder message.');
  parts.push('Include a subject line on the first line, then the body. Keep it concise and welcoming.');
  return parts.join(' ');
}

function parseGeneratedContent(content: string): { subject: string; body: string } {
  const lines = content.trim().split('\n');
  // First non-empty line is the subject, rest is body
  let subject = '';
  let bodyStart = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line) {
      subject = line.replace(/^(Subject:\s*)/i, '');
      bodyStart = i + 1;
      break;
    }
  }
  const body = lines.slice(bodyStart).join('\n').trim();
  return { subject, body };
}
